import React from 'react';
import {
    AlertCircle
} from 'lucide-react';
import ResourcePicker from '../../components/ResourcePicker';
import {
    KeyValueOelEditor, StepCommonAdvanced, StepCommonInlineFields,
    useProcedureStepIds
} from './_shared';
import {
    LlmStepZone, LlmLabeled, LlmStepCanvas
} from './llmStepLayout';

/**
 * InlineCallStep — kind:"call".
 * Invokes a definition by ref. Schema (V2StepCall.tsx + SCHEMAS.md §4):
 *   { call: { ref: <definition_id> }, inputs: { argName: oelStr, … } }
 */
export default function InlineCallStep({
    step, onPatchStep, definitions = [], oelContext
}) {
    const ref = step.call?.ref || '';
    const inputs = step.inputs || {};
    const stepIdOptions = useProcedureStepIds(oelContext);
    const patch = (p) => onPatchStep({ ...step, ...p });

    function setRef(newRef) {
        patch({
            call: {
                ...(step.call || {}),
                ref: newRef
            }
        });
    }
    function setInputs(next) {
        patch({ inputs: next });
    }

    return (
        <LlmStepCanvas>
            <LlmStepZone
                variant="configuration"
                title="Definition"
                description={`Pick a reusable definition from this opcode (${definitions.length} declared).`}
            >
                {definitions.length === 0 ? (
                    <div className="flex items-start gap-2 rounded-md border border-amber-300/50 bg-amber-50/40 px-3 py-2 text-[11.5px] text-amber-900">
                        <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                        <span>
                            No definitions declared on this opcode yet. Add one under the
                            opcode-level <b>Definitions</b> section (or use a different
                            kind like <b>subflow</b> for inline reusable logic).
                        </span>
                    </div>
                ) : (
                    <LlmLabeled
                        label="Reference"
                        help="Which definition to run — add definitions in the left panel under Definitions."
                    >
                        <ResourcePicker
                            kind="definition"
                            value={ref}
                            onChange={(id) => setRef(id)}
                            localItems={definitions}
                            placeholder="No definition selected"
                        />
                    </LlmLabeled>
                )}
            </LlmStepZone>

            <LlmStepZone
                variant="arguments"
                title="Arguments"
                description="Values passed into the definition — each value is an OEL expression."
            >
                <KeyValueOelEditor
                    value={inputs}
                    onChange={setInputs}
                    keyLabel="Argument"
                    addLabel="Add argument"
                    placeholderKey="arg_name"
                    placeholderValue="@input.field"
                    oelContext={oelContext}
                />
            </LlmStepZone>

            <StepCommonInlineFields
                step={step}
                onPatchStep={onPatchStep}
                stepIdOptions={stepIdOptions}
            />

            <StepCommonAdvanced
                step={step}
                onPatchStep={onPatchStep}
                oelContext={oelContext}
            />
        </LlmStepCanvas>
    );
}
